import { getCurrentInstance } from 'vue';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { useAuthStore } from '@/stores/auth';
import { bnHttpErrorHandler } from '@/common/api/http.error.notifications';
import { transformFhirResponse } from '@/common/api/transformFhirResponse';
import { wrapWithDedup, clearDedupCache } from '@/common/api/requestDedup';

var $httpFhirApi;
const timeoutValue = 90000;
export const makeHttpFhir = () => {
    let axiosConfig = {
        headers: {
            post: { Accept: 'application/json' },
            // prevent browsers from storing data in persistant storage, only memory
            // in cases where browsers store "no-cache" data in memory, advise it to validate it hasn't changed
            get: { Accept: 'application/json', 'cache-control': 'no-cache,no-store,must-revalidate,private', expires: 0, pragma: 'no-cache' },
            put: { Accept: 'application/json' },
            patch: { Accept: 'application/json' },
        },
        timeout: !import.meta.env.VITE_NO_TIMEOUT ? timeoutValue : 0,
    };

    if (window.location.host.includes('loca.lt')) {
        axiosConfig.baseURL = 'https://' + import.meta.env.VITE_MOBILE_TEST_NAME + '-bestnotes-fresh-aidbox.loca.lt';
    }

    var httpFhir = axios.create(axiosConfig);

    axiosRetry(httpFhir, { retries: 3 });

    // Check if data is a JSON Patch (RFC 6902) - array of patch operations
    const isJsonPatch = (data) => {
        return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'op' in data[0];
    };

    // Recursively check if a value is deeply empty (all leaves are null, undefined,
    // empty string, empty array, or empty object). Used to decide whether a patch
    // operation's value would result in Aidbox rejecting it as "empty-value".
    const isEmptyPatchValue = (value) => {
        if (value === null || value === undefined || value === '') return true;
        if (Array.isArray(value)) return value.length === 0 || value.every((item) => isEmptyPatchValue(item));
        if (typeof value === 'object') {
            const keys = Object.keys(value);
            return keys.length === 0 || keys.every((key) => isEmptyPatchValue(value[key]));
        }
        return false;
    };

    // Filter or clean patch operations that would send empty values to Aidbox.
    // - 'add' with a deeply-empty value is dropped (the field may not exist yet,
    //   so a 'remove' would fail with "Nothing to remove").
    // - 'replace' with a deeply-empty value is dropped (Aidbox rejects these with
    //   "empty-value" validation errors).
    const handleEmptyAddOperations = (data) => {
        if (!isJsonPatch(data)) {
            return data;
        }

        return data.filter((item) => {
            if (typeof item === 'object' && item !== null) {
                if ((item.op === 'add' || item.op === 'replace') && (!('value' in item) || isEmptyPatchValue(item.value))) {
                    return false;
                }
            }
            return true;
        });
    };

    // Helper function to clean up data by removing empty values
    const cleanEmptyValues = (obj) => {
        if (obj === null || obj === undefined || obj === '') {
            return undefined;
        }

        if (Array.isArray(obj)) {
            // Filter out empty values from arrays
            const cleaned = obj
                .map((item) => cleanEmptyValues(item))
                .filter((item) => {
                    if (item === null || item === undefined || item === '') return false;
                    if (Array.isArray(item)) return item.length > 0;
                    if (typeof item === 'object') {
                        return Object.keys(item).length > 0;
                    }
                    return true;
                });
            return cleaned.length > 0 ? cleaned : undefined;
        }

        if (typeof obj === 'object' && obj !== null) {
            const cleaned = {};
            let hasValues = false;

            if (typeof obj.toJSON === 'function') {
                obj = obj.toJSON();
            }

            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const cleanedValue = cleanEmptyValues(obj[key]);
                    if (cleanedValue !== undefined) {
                        cleaned[key] = cleanedValue;
                        hasValues = true;
                    }
                }
            }

            if (!hasValues) return undefined;
            // Strip FHIR references that have resourceType but no id
            if (cleaned.resourceType && !cleaned.id && Object.keys(cleaned).length === 1) return undefined;
            return cleaned;
        }

        return obj;
    };

    // Add a request interceptor
    httpFhir.interceptors.request.use(
        (config) => {
            const authStore = useAuthStore();

            if (authStore.token && !config?.url?.startsWith('https://bnprofile.fhir') && (!config?.terminology || import.meta.env.VITE_TERMINOLOGY_LOCAL)) {
                config.headers.setAuthorization('Bearer ' + authStore.token);
            }

            // If request is not a terminology request or we have TERMINOLOGY_LOCAL true, set baseURL
            if (!config?.terminology || import.meta.env.VITE_TERMINOLOGY_LOCAL) {
                config.baseURL = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri;
            }
            // Clean up data and skip reference validation for non-GET requests
            if (['post', 'patch', 'put', 'delete'].includes(config.method)) {
                config.headers['aidbox-validation-skip'] = 'reference';
                if (config.data) {
                    if (isJsonPatch(config.data)) {
                        config.data = handleEmptyAddOperations(config.data);
                    } else {
                        config.data = cleanEmptyValues(config.data);
                    }
                }
            }

            return config;
        },
        (err) => {
            // Do something with request error
            return Promise.reject(err);
        },
    );

    // Add a response interceptor
    httpFhir.interceptors.response.use(
        (res) => transformFhirResponse(res),
        async (err) => {
            return await bnHttpErrorHandler(err, timeoutValue);
        },
    );

    httpFhir.get = wrapWithDedup(httpFhir.get.bind(httpFhir));

    return httpFhir;
};

$httpFhirApi = makeHttpFhir();

export const updateHttpFhirService = () => {
    clearDedupCache();
    $httpFhirApi = makeHttpFhir();
    let app = getCurrentInstance();

    if (app?.appContext) {
        app.appContext.config.globalProperties.$httpFhirApi = $httpFhirApi; // update here whenever $fhirApi changes (token, apiUri)
    }
};

export { $httpFhirApi };
